import React, { useEffect, useState, useMemo } from 'react';
import { setHeader } from "../../store/settings/settingsSlice";
import { useLocation } from "react-router-dom";
import style from "./wallet.module.css";
import Modal from '../modal/Modal';
import successIcon from '../../assets/images/check-mark.svg'
import { useDispatch, useSelector } from "react-redux";
import { tradeOrderLimit, getUserCryptoTransactions } from "../../store/crypto/cryptoActions";
import { getCryptoCoins } from '../../store/settings/settingsActions';
import TradingViewWidget from './TradingChart';
import cogoToast from 'cogo-toast';
import { cancelOrderLimit } from "../../store/crypto/cryptoActions";
import { CloseCircle } from "iconsax-react";
import moment from "moment";

const OrderBookPage = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [activeOrderType, setActiveOrderType] = useState('limit');
    const [isSuccessModal, setSuccessModal] = useState(false);
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    const [buyPrice, setBuyPrice] = useState("");
    const [buyAmount, setBuyAmount] = useState("");
    const [sellPrice, setSellPrice] = useState("");
    const [sellAmount, setSellAmount] = useState("");
    const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isCancellingOrder, setIsCancellingOrder] = useState(false);
    const loading = useSelector((state) => state.crypto.loading);
    
    const { cryptocoins = [] } = useSelector((state) => state.settings);
    const { transactions, loading: txLoading, chartError,chartData } = useSelector((state) => state.crypto);

    const buyTotal = useMemo(() => (parseFloat(buyPrice)||0)*(parseFloat(buyAmount)||0), [buyPrice, buyAmount]);
    const sellTotal = useMemo(() => (parseFloat(sellPrice)||0)*(parseFloat(sellAmount)||0), [sellPrice, sellAmount]);

    // Get currency from URL params
    const params = new URLSearchParams(location.search);
    const currency = params.get("currency") || "BTC";

    const selectedCoinObj = cryptocoins.find(
        (c) =>
            (c.currency || c.code || "").toUpperCase() === (currency || "").toUpperCase()
    );
    const coinTypeId = selectedCoinObj?.coin_type_id || selectedCoinObj?.coinTypeId || null;

    // Get USDT balance from cryptocoins for Buy section
    const usdtCryptoBalance = parseFloat(
        cryptocoins.find((c) => (c.currency || c.code || "").toUpperCase() === "USDT")?.balance
    ) || 0;

    // Get selected crypto balance for Sell section
    const selectedCryptoBalance = parseFloat(selectedCoinObj?.balance) || 0;


    // Buy section: Calculate amount based on USDT balance and price
    const handleBuyPercentage = (percentage) => {
        const buyableAmount = (usdtCryptoBalance * (percentage / 100)) / parseFloat(buyPrice || 1);
        setBuyAmount(buyableAmount.toFixed(8));
    };

    // Sell section: Calculate amount based on selected crypto balance
    const handleSellPercentage = (percentage) => {
        const sellableAmount = selectedCryptoBalance * (percentage / 100);
        setSellAmount(sellableAmount.toFixed(8));
    };

    // Add this function to open drawer
    const openOrderDrawer = (order) => {
        setSelectedOrder(order);
        setOrderDrawerOpen(true);
    };

    // Fetch limit orders
    useEffect(() => {
        if (!coinTypeId) return;
        dispatch(getUserCryptoTransactions({
            mode: 'limit',
            coinType: currency,
            page: 0,
            per_page: 20,
            status: 'pending'
        }));
    }, [coinTypeId, dispatch, currency]);

    useEffect(() => {
    if (chartData?.marketTicker?.last_price) {
        const currentPrice = chartData.marketTicker.last_price.toString();
        setBuyPrice(currentPrice);
        setSellPrice(currentPrice);
    }
}, [chartData?.marketTicker?.last_price]);

    const placeLimitOrder = async (side) => {
        const priceVal = side === "buy" ? parseFloat(buyPrice) : parseFloat(sellPrice);
        const volumeVal = side === "buy" ? parseFloat(buyAmount) : parseFloat(sellAmount);
        
        if (!coinTypeId) {
            cogoToast.error("Invalid coin type. Please select a valid cryptocurrency.");
            return;
        }
        
        if (!priceVal || !volumeVal) {
            cogoToast.error("Please enter valid price and amount.");
            return;
        }

        // Balance validation
        if (side === "buy") {
            const requiredUsdt = priceVal * volumeVal;
            if (requiredUsdt > usdtCryptoBalance) {
                cogoToast.error(
                    `Insufficient USDT balance. You need ${(requiredUsdt - usdtCryptoBalance).toFixed(2)} USDT more.`
                );
                return;
            }
        } else if (side === "sell") {
            if (volumeVal > selectedCryptoBalance) {
                cogoToast.error(
                    `Insufficient ${currency} balance. You need ${(volumeVal - selectedCryptoBalance).toFixed(8)} ${currency} more.`
                );
                return;
            }
        }

        setIsPlacingOrder(true);
        try {
            const payload = {
                coinTypeId,
                price: Number(priceVal),
                totalToDebit: side === "buy" ? buyTotal : parseFloat(sellAmount),
                side: side === "buy" ? "buy" : "sell",
            };

            const res = await dispatch(tradeOrderLimit(payload));
            if (res?.type === "tradeOrderLimit/fulfilled") {
                setSuccessModal(true);
                setBuyAmount("");
                setSellAmount("");
                setBuyPrice("");
                setSellPrice("");
                // Refresh orders
                dispatch(getUserCryptoTransactions({
                    mode: 'limit',
                    coinType: currency,
                    page: 0,
                    per_page: 20,
                    status: 'pending'
                }));
            } else {
                console.error("Order failed", res);
            }
        } catch (err) {
            console.error("Order error", err);
        } finally {
            setIsPlacingOrder(false);
        }
    };

    // Add this function to handle cancel
    const handleCancelOrder = async () => {
        if (!selectedOrder) return;
        
        setIsCancellingOrder(true);
        try {
            const res = await dispatch(cancelOrderLimit({ orderId: selectedOrder.id }));
            if (res?.type === "cancelOrderLimit/fulfilled") {
                setOrderDrawerOpen(false);
                setSelectedOrder(null);
                // Refresh orders
                dispatch(getUserCryptoTransactions({
                    mode: 'limit',
                    coinType: currency,
                    page: 0,
                    per_page: 20,
                    status: 'pending'
                }));
            }
        } catch (err) {
            console.error("Cancel order error", err);
        } finally {
            setIsCancellingOrder(false);
        }
    };

    useEffect(() => {
        dispatch(setHeader(`${currency} Wallet`));
        dispatch(getCryptoCoins()); 
    }, [dispatch, currency]);

    return ( 
        <>

        {/* Error Alert Banner */}
          {chartError && (
            <div style={{
              backgroundColor: '#FEE2E2',
              border: '1px solid #FECACA',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '20px',
              marginTop: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#DC2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                flexShrink: 0,
              }}>
                !
              </div>
              <p style={{
                margin: 0,
                color: '#991B1B',
                fontSize: '14px',
                fontWeight: '500',
              }}>
                Coin not activated for limit order operation
              </p>
            </div>
          )}

          {/* ======== THE MODAL(SUCCESSFUL) */}
        <Modal
            canClose={true}
            modalOpen={isSuccessModal}
            setModalOpen={setSuccessModal}
        >
            <div>
                <div style={{display: 'flex', justifyContent: 'center', marginTop: '25px'}}>
                    <img src={successIcon} alt="spin circle" />
                </div>

                <div>
                    <h4 style={{color: '#40196D', fontWeight: '600', marginTop: '25px'}}>
                        Limit Order Successful
                    </h4>
                
                    <p style={{color: '#B6B6B6', maxWidth:'300px', margin: 'auto', marginTop: '15px'}}>
                        You've successfully placed a limit order for {currency}.
                    </p>
                </div>

                <button onClick={() => setSuccessModal(false)} style={{marginTop: '20px'}} className={style.save}>
                   Continue
                </button>
            </div>
        </Modal>

        <div className={style.orderBookContainer}>
            {/* Header Section */}
            <div className={style.orderBookHeader}>
                <div className={style.headerLeft}>
                    <div className={style.pairSelector}>
                        <span className={style.selectedPair}>{currency}/USDT</span>
                       
                    </div>
                    <div className={style.bitcoinPriceTag}>
                        <span className={style.bitcoinIcon}>●</span>
                        <span className={style.bitcoinPriceText}>{currency} Price</span>
                    </div>
                </div>
                {/* Header Right Section */}
<div className={style.headerRight}>
    <div className={style.mainPriceSection}>
        <div className={style.currentPriceValue}>
            ${parseFloat(chartData?.marketTicker?.last_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div className={style.currentPriceNaira}>
            Last Price ({currency})
        </div>
    </div>

    <div className={style.statsGrid}>
        {/* High */}
        <div className={style.statItem}>
            <div className={style.statLabel}>High</div>
            <div className={style.changeValues}>
                <span className={style.changeAmount}>
                 ${parseFloat(chartData?.marketTicker?.highest_bid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            </div>
        </div>

        {/* Low */}
        <div className={style.statItem}>
            <div className={style.statLabel}>Low</div>
            <div className={style.changeValues}>
                <span className={style.changeAmount}>
                 ${parseFloat(chartData?.marketTicker?.lowest_ask || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            </div>
        </div>

      
    </div>
</div>
            </div>

            {/* Main Content */}
            <div className={style.orderBookContent}>
                {/* Chart Section */}
                <div className={style.chartSection}>
                     <TradingViewWidget 
                        symbol={`${currency}USDT`} 
                        coinTypeId={coinTypeId}
                        exchange="BINANCE" 
                          height={450} 
                    />
                    
                    {/* Bottom Trading Panel */}
                    <div className={style.bottomTradingPanel}>
                        <div className={style.tradingTabs}>
                            <button 
                                className={`${style.tradingTab} ${activeOrderType === 'limit' ? style.activeTab : ''}`}
                                onClick={() => setActiveOrderType('limit')}
                            >
                                Limit
                            </button>
                        </div>

                        <div className={style.tradingInputs}>
                            {/* Buy Side */}
                            <div className={style.buySide}>

                                {/* Add this balance display */}
                                <div style={{ marginBottom: '10px', fontSize: '12px', color: '#999' }}>
                                    Available Balance: <span style={{ color: '#40196D', fontWeight: '600' }}>
                                        {usdtCryptoBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })} USDT
                                    </span>
                                </div>
                                
                                <div className={style.inputGroup}>
                                    <label className={style.inputLabel}>Price</label>
                                    <div className={style.inputContainer}>
                                        <input 
                                            type="number" 
                                            value={buyPrice} 
                                            className={style.priceInput}
                                            placeholder='0.00'
                                            onChange={(e) => setBuyPrice(e.target.value)}
                                        />
                                        <span className={style.inputUnit}>USDT</span>
                                    </div>
                                </div>
                                
                                <div className={style.inputGroup}>
                                    <label className={style.inputLabel}>Amount</label>
                                    <div className={style.inputContainer}>
                                        <input 
                                            type="number" 
                                            value={buyAmount} 
                                            className={style.amountInput}
                                            placeholder="0.00"
                                            onChange={(e) => setBuyAmount(e.target.value)}
                                        />
                                        <span className={style.inputUnit}>{currency}</span>
                                    </div>
                                </div>

                                {/* Percentage buttons for Buy */}
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                                    <button onClick={() => handleBuyPercentage(10)} style={{ flex: '1', minWidth: '45px', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>10%</button>
                                    <button onClick={() => handleBuyPercentage(25)} style={{ flex: '1', minWidth: '45px', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>25%</button>
                                    <button onClick={() => handleBuyPercentage(50)} style={{ flex: '1', minWidth: '45px', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>50%</button>
                                    <button onClick={() => handleBuyPercentage(75)} style={{ flex: '1', minWidth: '45px', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>75%</button>
                                    <button onClick={() => handleBuyPercentage(100)} style={{ flex: '1', minWidth: '45px', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>Max</button>
                                </div>
                            
                                
                                <div className={style.totalSection}>
                                    <span className={style.totalLabel}>Total</span>
                                    <span className={style.totalValue}>{buyTotal.toLocaleString()} USDT</span>
                                </div>
                                
                                <button
                                    className={style.buyButton}
                                    onClick={() => placeLimitOrder("buy")}
                                    disabled={isPlacingOrder || chartError}>
                                    {isPlacingOrder ? "Placing..." : `Buy ${currency}`}    
                                </button>
                            </div>

                            {/* Sell Side */}
                            <div className={style.sellSide}>
                                
                                 {/* Add this balance display */}
                                    <div style={{ marginBottom: '10px', fontSize: '12px', color: '#999' }}>
                                        Available Balance: <span style={{ color: '#40196D', fontWeight: '600' }}>
                                            {selectedCryptoBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })} {currency}
                                        </span>
                                    </div>
                                <div className={style.inputGroup}>
                                    <label className={style.inputLabel}>Price</label>
                                    <div className={style.inputContainer}>
                                        <input 
                                            type="number" 
                                            value={sellPrice} 
                                            placeholder='0.00'
                                            onChange={(e) => setSellPrice(e.target.value)} 
                                            className={style.priceInput}
                                        />
                                        <span className={style.inputUnit}>USDT</span>
                                    </div>
                                </div>
                                
                                <div className={style.inputGroup}>
                                    <label className={style.inputLabel}>Amount</label>
                                    <div className={style.inputContainer}>
                                        <input 
                                            type="number" 
                                            value={sellAmount} 
                                            onChange={(e) => setSellAmount(e.target.value)} 
                                            className={style.amountInput}
                                            placeholder="0.00"
                                        />
                                        <span className={style.inputUnit}>{currency}</span>
                                    </div>
                                </div>

                                  {/* Percentage buttons for Sell */}
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                                        <button onClick={() => handleSellPercentage(10)} style={{ flex: '1', minWidth: '45px', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>10%</button>
                                        <button onClick={() => handleSellPercentage(25)} style={{ flex: '1', minWidth: '45px', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>25%</button>
                                        <button onClick={() => handleSellPercentage(50)} style={{ flex: '1', minWidth: '45px', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>50%</button>
                                        <button onClick={() => handleSellPercentage(75)} style={{ flex: '1', minWidth: '45px', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>75%</button>
                                        <button onClick={() => handleSellPercentage(100)} style={{ flex: '1', minWidth: '45px', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5' }}>Max</button>
                                    </div>
                              
                                
                                <div className={style.totalSection}>
                                    <span className={style.totalLabel}>Total</span>
                                    <span className={style.totalValue}>{sellTotal.toLocaleString()} USDT</span>
                                </div>
                                
                                <button
                                    className={style.sellButton}
                                    onClick={() => placeLimitOrder("sell")}
                                    disabled={isPlacingOrder || chartError}
                                >
                                    {isPlacingOrder ? "Placing..." : `Sell ${currency}`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Order Book */}
                <div className={style.rightPanel}>
                    <div className={style.orderBookTabs}>
                        <button 
                            className={`${style.obTab} ${style.activeObTab}`}
                        >
                            Order Book
                        </button>
                    </div>

                    <div className={style.orderBookTable}>
                        <div className={style.tableHeaders}>
                            <span>Currency</span>
                            <span>Amount</span>
                            <span>State</span>
                        </div>

                        {txLoading ? (
                            <div style={{padding: '20px', textAlign: 'center', color: '#888'}}>
                                Loading orders...
                            </div>
                        ) : (transactions?.items || []).length > 0 ? (
                            <div className={style.sellOrdersSection}>
                             {(transactions?.items || []).slice(0, 14).map((order, index) => (
                                <div 
                                    key={order.id || index} 
                                    className={style.orderRowSell}
                                    onClick={() => openOrderDrawer(order)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span className={style.redPrice}>{currency}/USDT</span>
                                    <span className={style.amount}>{parseFloat(order.data.amount_expected)} ({order.data.side})</span>
                                    <span className={style.total}>{order.status === "PENDING" ? 'OPEN' :  order.status}</span>
                                </div>
                            ))}
                            </div>
                        ) : (
                            <div style={{padding: '20px', textAlign: 'center', color: '#888'}}>
                                No transactions
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>

       {/* ============== ORDER DETAIL DRAWER */}
        <div
            style={{ right: orderDrawerOpen === true ? "0" : "-380px" }}
            className={style.orderDetailDrawer}
        >
            <div className={style.orderDrawerWrap}>
                <div className={style.orderDrawerTop}>
                    <h5>Order Details</h5>
                    <CloseCircle
                        onClick={() => {
                            setOrderDrawerOpen(false);
                            setSelectedOrder(null);
                        }}
                        className={style.closeIcon}
                    />
                </div>
                <div className={style.orderDrawerContent}>
                    {selectedOrder ? (
                        <div className={style.orderDrawerBody}>
                            {/* Order Header */}
                            <div className={style.orderHeader}>
                                <div className={style.orderPairBadge}>
                                    {currency}/USDT
                                </div>
                                <div className={`${style.orderStatus} ${style[selectedOrder.status.toLowerCase()]}`}>
                                    {selectedOrder.status === "PENDING" ? 'OPEN' : selectedOrder.status}
                                </div>
                            </div>

                            {/* Order Amount */}
                            <div className={style.orderAmount}>
                                <p className={style.amountLabel}>Amount</p>
                                <h3 className={style.amountValue}>
                                 {(parseFloat(selectedOrder.data.amount_expected) || 0)} {selectedOrder.data.side === "sell" ? "USDT" : currency}
                                </h3>
                            </div>

                            {/* Order Details */}
                            <div className={style.orderDetailsSection}>

                                 <div className={style.orderDetailItem}>
                                    <span className={style.detailLabel}>Side</span>
                                    <span className={style.detailValue}>
                                        {selectedOrder.data.side === 'buy' ? 'Buy' : 'Sell'}
                                    </span>
                                </div>

                                <div className={style.orderDetailItem}>
                                    <span className={style.detailLabel}>Price</span>
                                    <span className={style.detailValue}>
                                        {parseFloat(selectedOrder.data.price || 0).toFixed(2)}
                                    </span>
                                </div>

                               
                                <div className={style.orderDetailItem}>
                                    <span className={style.detailLabel}>Volume</span>
                                    <span className={style.detailValue}>
                                     {selectedOrder.data.side === "sell" ? (parseFloat(selectedOrder.data.amount_debited) || 0) : (parseFloat(selectedOrder.amount) || 0)} {currency}
                                    </span>
                                </div>

                                  <div className={style.orderDetailItem}>
                                    <span className={style.detailLabel}>Total</span>
                                    <span className={style.detailValue}>
                                        {selectedOrder.data.side === "sell" ? (parseFloat(selectedOrder.amount) || 0) : (parseFloat(selectedOrder.data.amount_debited) || 0)} 
                                    </span>
                                </div>

                                <div className={style.orderDetailItem}>
                                    <span className={style.detailLabel}>Expected Fees</span>
                                    <span className={style.detailValue}>
                                        {(parseFloat(selectedOrder.fee) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })} {selectedOrder.data.side === "sell" ? "USDT" : currency}
                                    </span>
                                    </div>


                                  <div className={style.orderDetailItem}>
                                    <span className={style.detailLabel}>Amount to be received</span>
                                    <span className={style.detailValue}>
                                        {(parseFloat(selectedOrder.data.amount_expected) || 0)} {selectedOrder.data.side === "sell" ? "USDT" : currency}
                                    </span>
                                </div>

                               

                                <div className={style.orderDetailItem}>
                                    <span className={style.detailLabel}>Date Created</span>
                                    <span className={style.detailValue}>
                                        {selectedOrder.created_at 
                                            ? moment(selectedOrder.created_at).format("Do MMM YYYY, hh:mm a")
                                            : "N/A"
                                        }
                                    </span>
                                </div>

                                <div className={style.orderDetailItem}>
                                    <span className={style.detailLabel}>Order ID</span>
                                    <span className={style.detailValue}>{selectedOrder.id}</span>
                                </div>
                            </div>

                            {/* Cancel Button - Only show if status is pending */}
                            {selectedOrder.status.toLowerCase() === 'pending' && (
                                <button 
                                    className={style.cancelButton}
                                    onClick={handleCancelOrder}
                                    disabled={isCancellingOrder || loading}
                                >
                                    {isCancellingOrder ? "Cancelling..." : "Cancel Order"}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                            No order selected
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
}
 
export default OrderBookPage;